// repo\primary-care-connect\frontend\src\pages\public\PatientRequest\PatientRequestPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import {
  ApiError,
  createPatientRequest,
  getFamilyDoctorPublicProfile,
  getPatientRequestReasons,
  PatientRequestReasonItem,
} from "../../../api/familyDoctors";
import "./PatientRequestPage.css";

// Smooth scroll to first error field
function scrollToFirstError() {
  const firstError = document.querySelector(".pr-invalid, [aria-invalid='true']");
  if (!firstError) return;
  firstError.scrollIntoView({ behavior: "smooth", block: "center" });
  setTimeout(() => {
    if (firstError instanceof HTMLElement) firstError.focus();
  }, 300);
}

function ValidationIcon({ valid, show }: { valid: boolean; show: boolean }) {
  if (!show) return null;
  return (
    <span
      className={`pr-validation-icon ${
        valid ? "pr-validation-icon--valid" : "pr-validation-icon--invalid"
      }`}
    >
      {valid ? "✓" : ""}
    </span>
  );
}

function getValidationClass(touched: boolean, isValid: boolean, value: string): string {
  if (!touched || !value.trim()) return "";
  return isValid ? "pr-valid" : "pr-invalid";
}

function asNumberId(id?: string): number | null {
  const n = parseInt(String(id || ""), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function asPositiveInt(val: string): number | null {
  const n = parseInt(String(val || "").trim(), 10);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatPostalCodeInput(raw: string): string {
  const s = String(raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 6);
  return s.length <= 3 ? s : `${s.slice(0, 3)} ${s.slice(3)}`;
}

function formatPhoneInput(raw: string): string {
  let digits = String(raw || "").replace(/\D/g, "");

  // ✅ If the user includes the North American country code "1", drop it.
  // Examples: "1 (416) 999-0000", "+1 4169990000", "14169990000"
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }

  // Keep max 10 digits for formatting
  digits = digits.slice(0, 10);

  const a = digits.slice(0, 3),
    b = digits.slice(3, 6),
    c = digits.slice(6, 10);

  if (digits.length === 0) return "";
  if (digits.length < 4) return `(${a}`;
  if (digits.length < 7) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

function isValidPostalCode(v: string): boolean {
  const s = String(v || "").toUpperCase().replace(/\s/g, "");
  return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(s);
}

function isValidPhone(v: string): boolean {
  return /^\(\d{3}\)\s\d{3}-\d{4}$/.test(String(v || "").trim());
}

function normalizeEmail(raw: string): string {
  return String(raw || "").trim().toLowerCase();
}

function isValidEmail(v: string): boolean {
  const s = normalizeEmail(v);
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
}

type RequestForOption = "Myself" | "MyChild" | "MyFamily";
type PatientGenderOption = "Female" | "Male" | "Non-binary" | "Prefer not to say";
type ModalState =
  | null
  | { kind: "success" | "warning" | "error"; title: string; message: string };
type CoverageTypeItem = { CoverageTypeID: number; Name: string };

const PATIENT_REQUEST_CONSENT_VERSION = "v3";

export default function PatientRequestPage() {
  const { id } = useParams<{ id: string }>();
  const numericId = useMemo(() => asNumberId(id), [id]);
  const backToProfileHref = id ? `/public/family-doctor/${id}` : "/public/find-family-doctor";

  // doctor profile
  const [doctorName, setDoctorName] = useState("");
  const [clinicName, setClinicName] = useState("");
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [doctorError, setDoctorError] = useState<string | null>(null);

  // reasons
  const [reasonsLoading, setReasonsLoading] = useState(true);
  const [reasonsError, setReasonsError] = useState<string | null>(null);
  const [reasons, setReasons] = useState<PatientRequestReasonItem[]>([]);

  // coverage
  const [coverageLoading, setCoverageLoading] = useState(true);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  const [coverageTypes, setCoverageTypes] = useState<CoverageTypeItem[]>([]);

  // submit
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState<number | null>(null);
  const [duplicateRequestId, setDuplicateRequestId] = useState<number | null>(null);
  const [modal, setModal] = useState<ModalState>(null);

  // form fields
  const [requestFor, setRequestFor] = useState<RequestForOption | "">("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState(""); // YYYY-MM-DD
  const [patientGender, setPatientGender] = useState<PatientGenderOption | "">("");
  const [applicantPhone, setApplicantPhone] = useState(""); // formatted
  const [applicantEmail, setApplicantEmail] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [reasonId, setReasonId] = useState("");
  const [coverageTypeId, setCoverageTypeId] = useState("");
  const [consent, setConsent] = useState(false);

  // touched
  const [touchedPhone, setTouchedPhone] = useState(false);
  const [touchedEmail, setTouchedEmail] = useState(false);
  const [touchedPostal, setTouchedPostal] = useState(false);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      if (!numericId) {
        setDoctorName("");
        setClinicName("");
        setDoctorError(null);
        return;
      }
      try {
        setDoctorLoading(true);
        setDoctorError(null);
        const prof: any = await getFamilyDoctorPublicProfile(numericId);
        if (cancelled) return;

        setDoctorName(String(prof?.FullName || "").trim());
        setClinicName(
          String(prof?.ClinicName || prof?.Clinic?.ClinicName || prof?.Clinic?.Name || "").trim()
        );
      } catch (e: any) {
        if (cancelled) return;
        setDoctorError(
          e instanceof ApiError ? e.message || "Failed to load doctor profile." : "Failed to load doctor profile."
        );
        setDoctorName("");
        setClinicName("");
      } finally {
        if (!cancelled) setDoctorLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [numericId]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setReasonsLoading(true);
        setReasonsError(null);
        const list = await getPatientRequestReasons();
        if (cancelled) return;
        setReasons(Array.isArray(list) ? list : []);
      } catch (e: any) {
        if (cancelled) return;
        setReasonsError(e instanceof ApiError ? e.message || "Failed to load reasons." : "Failed to load reasons.");
        setReasons([]);
      } finally {
        if (!cancelled) setReasonsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setCoverageLoading(true);
        setCoverageError(null);
        const res = await fetch("/api/coverage-types", {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) throw new Error(`Failed to load coverage types (${res.status})`);
        const data = await res.json();
        if (cancelled) return;
        setCoverageTypes(Array.isArray(data) ? data : []);
      } catch (e: any) {
        if (cancelled) return;
        setCoverageError(e?.message ? String(e.message) : "Failed to load coverage types.");
        setCoverageTypes([]);
      } finally {
        if (!cancelled) setCoverageLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const postalOk = postalCode.trim().length > 0 && isValidPostalCode(postalCode);
  const phoneOk = applicantPhone.trim().length > 0 && isValidPhone(applicantPhone);
  const normalizedEmail = normalizeEmail(applicantEmail);
  const emailOk = normalizedEmail.length > 0 && isValidEmail(normalizedEmail);
  const coverageOk = coverageTypeId.trim().length > 0;
  const genderOk = String(patientGender).trim().length > 0;

  const canSubmit =
    !!numericId &&
    requestFor.trim().length > 0 &&
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    dateOfBirth.trim().length > 0 &&
    genderOk &&
    phoneOk &&
    emailOk &&
    postalOk &&
    reasonId.trim().length > 0 &&
    coverageOk &&
    consent === true &&
    !submitting;

  const requestingDoctorLabel = doctorLoading ? "Loading…" : doctorName ? doctorName : id ? `Profile #${id}` : "—";
  const requestingClinicLabel = doctorLoading ? "" : clinicName ? clinicName : "";

  return (
    <div className="ocf-page">
      <SEO
        title="Request to Become a Patient"
        description="Submit a request to become a patient with a participating family doctor on AcceptingMD."
        noIndex={true}
      />
      <PublicHeader variant="solid" />

      {modal ? (
        <div
          role="dialog"
          aria-modal="true"
          className="pr-modal-overlay"
          onClick={() => {
            if (modal.kind === "warning") return;
            setModal(null);
          }}
        >
          <div
            className={`pr-modal ${
              modal.kind === "success"
                ? "pr-modal--success"
                : modal.kind === "warning"
                ? "pr-modal--warning"
                : "pr-modal--error"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="pr-modal-close" onClick={() => setModal(null)} aria-label="Close">
              ×
            </button>

            <div className="pr-modal-icon">
              {modal.kind === "success" ? (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="currentColor" fillOpacity="0.12" />
                  <path d="M14 24L18 28L22 24" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 18H34V32C34 33.1046 33.1046 34 32 34H16C14.8954 34 14 33.1046 14 32V18Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  <path d="M14 18L24 26L34 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : modal.kind === "warning" ? (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="currentColor" fillOpacity="0.12" />
                  <path d="M24 10L39 36H9L24 10Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M24 18V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="24" cy="30.5" r="1.5" fill="currentColor" />
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="currentColor" fillOpacity="0.12" />
                  <circle cx="24" cy="24" r="12" stroke="currentColor" strokeWidth="2" />
                  <path d="M24 18V26" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
                  <circle cx="24" cy="30" r="1.5" fill="currentColor" />
                </svg>
              )}
            </div>

            <div className="pr-modal-title">{modal.title}</div>

            {modal.kind === "warning" && modal.title === "Email verification required" ? (
              <div className="pr-modal-body">
                <p className="pr-modal-message">We've sent a verification link to your email address.</p>
                <div className="pr-modal-highlight">
                  <strong>Your request will not be sent to the clinic until you verify your email.</strong>
                </div>
                <p className="pr-modal-hint">
                  If you don't see the email within a few minutes, please check your <strong>Spam</strong> or{" "}
                  <strong>Junk</strong> folder.
                </p>
              </div>
            ) : (
              <div className="pr-modal-message">{modal.message}</div>
            )}

            <div className="pr-modal-actions">
              <button type="button" className="pr-btn pr-btn-primary" onClick={() => setModal(null)}>
                {modal.kind === "success" ? "Got it" : modal.kind === "warning" ? "I’ll verify my email now" : "OK"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="ocf-container">
        <div className="pr-card">
          <div className="pr-breadcrumbs">
            <Link to={backToProfileHref} className="pr-breadcrumb-link">
              ← Back to profile
            </Link>
            <span className="pr-breadcrumb-sep">·</span>
            <Link to="/public/find-family-doctor" className="pr-breadcrumb-link">
              Back to results
            </Link>
          </div>

          <h1 className="pr-title pr-title--tight">Request to Become a Patient</h1>

          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <div className="pr-doctorline">
              <span className="pr-doctorline-label">Doctor:</span>{" "}
              <span className="pr-doctorline-name">{requestingDoctorLabel}</span>
            </div>
            {requestingClinicLabel ? <div className="pr-clinic-label">{requestingClinicLabel}</div> : null}
          </div>

          {doctorError ? <div className="pr-help pr-help--error">Could not load doctor details: {doctorError}</div> : null}

          {!numericId ? (
            <div className="pr-error">
              <b>Invalid profile ID.</b> Please go back and select a doctor from results.
            </div>
          ) : null}
        </div>

        {submitted ? (
          <div className="pr-card">
            <div className="pr-steps">
              <div className="pr-steps-title">What happens next</div>

              <div className="pr-step pr-step-highlight">
                <div className="pr-step-badge">1</div>
                <div className="pr-step-content">
                  <div className="pr-step-title">Check your email</div>
                  <p className="pr-step-desc">
                    We sent a verification email to <b>{normalizedEmail}</b>. Please wait a few minutes. If you don't see
                    it, check your <b>Spam / Junk</b> folder.
                  </p>
                </div>
              </div>

              <div className="pr-step">
                <div className="pr-step-badge">2</div>
                <div className="pr-step-content">
                  <div className="pr-step-title">Confirm your request</div>
                  <p className="pr-step-desc">
                    Open the email and click <b>"Confirm request"</b>. Your request will not be sent to the clinic until you confirm your email.
                  </p>
                </div>
              </div>

              <div className="pr-step">
                <div className="pr-step-badge">3</div>
                <div className="pr-step-content">
                  <div className="pr-step-title">Clinic review</div>
                  <p className="pr-step-desc">
                    After verification, the clinic will review your request based on its current capacity, practice scope, and intake criteria.
                    Submitting a request does not guarantee attachment to the practice.
                  </p>
                </div>
              </div>
            </div>

            {submittedRequestId ? (
              <div className="pr-request-id">
                Request ID: <b>{submittedRequestId}</b>
              </div>
            ) : null}

            <div className="pr-muted-13 pr-muted-13--mt10 pr-muted-13--lh155">
              After verification, you will receive a confirmation email with a secure link to view, manage, or cancel your request.
            </div>

            <div className="pr-actions pr-span-2 pr-muted-13--mt16">
              <Link to={backToProfileHref} className="pr-btn pr-btn-primary">
                Back to profile
              </Link>
              <Link to="/public/find-family-doctor" className="pr-btn">
                Find another doctor
              </Link>
            </div>
          </div>
        ) : (
          <form
            className="pr-card pr-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (submitting) return;

              setSubmitError(null);
              setDuplicateRequestId(null);
              setTouchedPhone(true);
              setTouchedEmail(true);
              setTouchedPostal(true);

              if (!numericId) {
                const msg = "Invalid doctor profile ID.";
                setSubmitError(msg);
                setModal({
                  kind: "error",
                  title: "Could not submit",
                  message: msg + " Please go back and select a doctor from results, then try again.",
                });
                return;
              }
              if (!requestFor)
                return setModal({ kind: "error", title: "Missing information", message: "Please choose who this request is for." });
              if (!firstName.trim())
                return setModal({ kind: "error", title: "Missing information", message: "Please enter a first name." });
              if (!lastName.trim())
                return setModal({ kind: "error", title: "Missing information", message: "Please enter a last name." });
              if (!dateOfBirth.trim())
                return setModal({ kind: "error", title: "Missing information", message: "Please enter a date of birth." });
              if (!patientGender)
                return setModal({ kind: "error", title: "Missing information", message: "Please select the patient’s gender." });

              if (!phoneOk) {
                setModal({
                  kind: "error",
                  title: "Invalid phone number",
                  message: "Please enter a valid 10-digit phone number in this format: (416) 999-0000.",
                });
                setTimeout(scrollToFirstError, 100);
                return;
              }
              if (!emailOk) {
                setModal({
                  kind: "error",
                  title: "Invalid email address",
                  message: "Please enter a valid email address in this format: patient@example.com.",
                });
                setTimeout(scrollToFirstError, 100);
                return;
              }
              if (!postalOk) {
                setModal({
                  kind: "error",
                  title: "Invalid postal code",
                  message: "Please enter a valid postal code in this format: L5X 1E1.",
                });
                setTimeout(scrollToFirstError, 100);
                return;
              }
              if (!reasonId.trim())
                return setModal({ kind: "error", title: "Missing information", message: "Please select a reason." });
              if (!coverageTypeId.trim())
                return setModal({ kind: "error", title: "Missing information", message: "Please select your health coverage type." });
              if (!consent)
                return setModal({ kind: "error", title: "Consent required", message: "Please confirm the consent statement to submit your request." });

              const rid = asPositiveInt(reasonId);
              if (!rid) return setModal({ kind: "error", title: "Invalid selection", message: "Please select a valid reason." });

              const covId = asPositiveInt(coverageTypeId);
              if (!covId) return setModal({ kind: "error", title: "Invalid selection", message: "Please select a valid health coverage type." });

              try {
                setSubmitting(true);

                const payload: any = {
                  familyDoctorId: numericId,
                  requestFor,
                  firstName: firstName.trim(),
                  lastName: lastName.trim(),
                  applicantEmail: normalizedEmail,
                  applicantPhone: applicantPhone.trim(),
                  patientGender,
                  dateOfBirth: dateOfBirth.trim(),
                  postalCode: postalCode.trim(),
                  reasonIds: [rid],
                  coverageTypeId: covId,
                  consentAccepted: consent === true,
                  consentVersion: PATIENT_REQUEST_CONSENT_VERSION,
                };

                const resp = await createPatientRequest(payload);
                setSubmittedRequestId(resp?.registrationRequestId ?? null);
                setSubmitted(true);
                setModal({
                  kind: "warning",
                  title: "Email verification required",
                  message:
                    "We sent you an email verification link. Your request will not be received by the clinic until you confirm your email. " +
                    "If you don’t see the email within a few minutes, please check your Spam / Junk folder.",
                });
              } catch (err: any) {
                if (err instanceof ApiError && err.status === 409) {
                  const existingId = err.payload?.existingRequestId;
                  const existingNum =
                    typeof existingId === "number"
                      ? existingId
                      : Number.isFinite(Number(existingId))
                      ? Number(existingId)
                      : null;

                  setDuplicateRequestId(existingNum);

                  const msg =
                    "A pending request already exists for this clinic. Please wait for a response or cancel your existing request before submitting another to the same clinic.";
                  setSubmitError(msg);

                  setModal({
                    kind: "error",
                    title: "Request already submitted",
                    message: msg + (existingNum ? ` (Existing request ID: ${existingNum})` : ""),
                  });
                } else if (err instanceof ApiError) {
                  const msg = err.message || "Submission failed.";
                  setSubmitError(msg);
                  setModal({ kind: "error", title: "Could not submit", message: msg + " Please review the form and submit again." });
                } else {
                  const msg = "Submission failed.";
                  setSubmitError(msg);
                  setModal({ kind: "error", title: "Could not submit", message: msg + " Please review the form and submit again." });
                }
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {submitError ? (
              <div className="pr-error">
                <b>Could not submit.</b> {submitError}
                {duplicateRequestId ? (
                  <div className="pr-muted-13 pr-muted-13--mt10">
                    Existing pending request ID: <b>{duplicateRequestId}</b>
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="pr-section pr-span-2">
              <h2 className="pr-section-title">About the patient</h2>

              <div className="pr-field pr-span-2">
                <label className="pr-label">This request is for *</label>
                <div className="pr-choicebox">
                  <label className="pr-radio">
                    <input type="radio" name="requestFor" disabled={submitting} checked={requestFor === "Myself"} onChange={() => setRequestFor("Myself")} />
                    Myself
                  </label>
                  <label className="pr-radio">
                    <input type="radio" name="requestFor" disabled={submitting} checked={requestFor === "MyChild"} onChange={() => setRequestFor("MyChild")} />
                    My Minor Child
                  </label>
                  <label className="pr-radio">
                    <input type="radio" name="requestFor" disabled={submitting} checked={requestFor === "MyFamily"} onChange={() => setRequestFor("MyFamily")} />
                    My Adult Family Member
                  </label>
                </div>
              </div>

              <div className="pr-row pr-row-3 pr-span-2">
                <div className="pr-field">
                  <label className="pr-label">First name *</label>
                  <input className="pr-input" type="text" value={firstName} disabled={submitting} onChange={(e) => setFirstName(e.target.value)} placeholder="e.g., Jane" autoComplete="given-name" />
                </div>
                <div className="pr-field">
                  <label className="pr-label">Last name *</label>
                  <input className="pr-input" type="text" value={lastName} disabled={submitting} onChange={(e) => setLastName(e.target.value)} placeholder="e.g., Doe" autoComplete="family-name" />
                </div>
                <div className="pr-field">
                  <label className="pr-label">Date of birth *</label>
                  <input className="pr-input" type="date" value={dateOfBirth} disabled={submitting} onChange={(e) => setDateOfBirth(e.target.value)} />
                </div>
              </div>

              <div className="pr-field pr-span-2">
                <label className="pr-label">Patient gender *</label>
                <div className="pr-choicebox">
                  <label className="pr-radio">
                    <input type="radio" name="patientGender" disabled={submitting} checked={patientGender === "Female"} onChange={() => setPatientGender("Female")} />
                    Female
                  </label>
                  <label className="pr-radio">
                    <input type="radio" name="patientGender" disabled={submitting} checked={patientGender === "Male"} onChange={() => setPatientGender("Male")} />
                    Male
                  </label>
                  <label className="pr-radio">
                    <input type="radio" name="patientGender" disabled={submitting} checked={patientGender === "Non-binary"} onChange={() => setPatientGender("Non-binary")} />
                    Non-binary
                  </label>
                  <label className="pr-radio">
                    <input type="radio" name="patientGender" disabled={submitting} checked={patientGender === "Prefer not to say"} onChange={() => setPatientGender("Prefer not to say")} />
                    Prefer not to say
                  </label>
                </div>
                <div className="pr-helper">Please select one option to proceed.</div>
              </div>
            </div>

            <div className="pr-section pr-span-2">
              <h2 className="pr-section-title">Contact details</h2>

              <div className="pr-row pr-row-3 pr-span-2">
                <div className="pr-field">
                  <label className="pr-label">Phone *</label>
                  <div className="pr-input-wrap">
                    <input
                      className={`pr-input ${getValidationClass(touchedPhone, phoneOk, applicantPhone)}`}
                      type="tel"
                      value={applicantPhone}
                      disabled={submitting}
                      onChange={(e) => setApplicantPhone(formatPhoneInput(e.target.value))}
                      onBlur={() => setTouchedPhone(true)}
                      placeholder="(416) 999-0000"
                      inputMode="tel"
                      autoComplete="tel"
                      aria-invalid={touchedPhone && !phoneOk ? true : undefined}
                    />
                    <ValidationIcon valid={phoneOk} show={touchedPhone && applicantPhone.length > 0} />
                  </div>
                  {touchedPhone && !phoneOk ? (
                    <div className="pr-helper pr-help--error">
                      Enter a valid phone number (10 digits) in this format: (416) 999-0000.
                    </div>
                  ) : (
                    <div className="pr-helper">Format: (416) 999-0000</div>
                  )}
                </div>

                <div className="pr-field">
                  <label className="pr-label">Email *</label>
                  <div className="pr-input-wrap">
                    <input
                      className={`pr-input ${getValidationClass(touchedEmail, emailOk, applicantEmail)}`}
                      type="email"
                      value={applicantEmail}
                      disabled={submitting}
                      onChange={(e) => setApplicantEmail(e.target.value)}
                      onBlur={() => {
                        setTouchedEmail(true);
                        setApplicantEmail(normalizeEmail(applicantEmail));
                      }}
                      placeholder="patient@example.com"
                      autoComplete="email"
                      inputMode="email"
                      aria-invalid={touchedEmail && !emailOk ? true : undefined}
                    />
                    <ValidationIcon valid={emailOk} show={touchedEmail && applicantEmail.length > 0} />
                  </div>
                  {touchedEmail && !emailOk ? (
                    <div className="pr-helper pr-help--error">
                      Enter a valid email address in this format: patient@example.com.
                    </div>
                  ) : (
                    <div className="pr-helper">We'll email you a verification link.</div>
                  )}
                </div>

                <div className="pr-field">
                  <label className="pr-label">Postal code *</label>
                  <div className="pr-input-wrap">
                    <input
                      className={`pr-input ${getValidationClass(touchedPostal, postalOk, postalCode)}`}
                      type="text"
                      value={postalCode}
                      disabled={submitting}
                      onChange={(e) => setPostalCode(formatPostalCodeInput(e.target.value))}
                      onBlur={() => setTouchedPostal(true)}
                      placeholder="L0X 6D1"
                      inputMode="text"
                      autoComplete="postal-code"
                      aria-invalid={touchedPostal && !postalOk ? true : undefined}
                    />
                    <ValidationIcon valid={postalOk} show={touchedPostal && postalCode.length > 0} />
                  </div>
                  {touchedPostal && !postalOk ? (
                    <div className="pr-helper pr-help--error">Enter a valid postal code in this format: L0X 6D1.</div>
                  ) : (
                    <div className="pr-helper">Format: L0X 6D1</div>
                  )}
                </div>
              </div>
            </div>

            <div className="pr-section pr-span-2">
              <h2 className="pr-section-title">Request details</h2>

              <div className="pr-field pr-span-2">
                <label className="pr-label">Health coverage type *</label>
                <select className="pr-select" value={coverageTypeId} onChange={(e) => setCoverageTypeId(e.target.value)} disabled={coverageLoading || submitting}>
                  <option value="" disabled>
                    {coverageLoading ? "Loading…" : "Select one…"}
                  </option>
                  {coverageTypes.map((c) => (
                    <option key={c.CoverageTypeID} value={String(c.CoverageTypeID)}>
                      {c.Name}
                    </option>
                  ))}
                </select>

                {coverageError ? (
                  <div className="pr-alert" role="alert">
                    <div className="pr-alert-title">We couldn’t load coverage types</div>
                    <div>{coverageError ? `Details: ${coverageError}` : "Please try again."}</div>
                    <div className="pr-alert-actions">
                      <button
                        type="button"
                        className="pr-btn pr-btn-link"
                        disabled={coverageLoading || submitting}
                        onClick={async () => {
                          try {
                            setCoverageLoading(true);
                            setCoverageError(null);

                            const res = await fetch("/api/coverage-types", {
                              method: "GET",
                              headers: { "Content-Type": "application/json" },
                            });

                            if (!res.ok) throw new Error(`Failed to load coverage types (${res.status})`);
                            const data = await res.json();
                            setCoverageTypes(Array.isArray(data) ? data : []);
                          } catch (e: any) {
                            setCoverageError(e?.message ? String(e.message) : "Failed to load coverage types.");
                            setCoverageTypes([]);
                          } finally {
                            setCoverageLoading(false);
                          }
                        }}
                      >
                        Try again
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="pr-field pr-span-2">
                <label className="pr-label">Reason for requesting a family doctor *</label>
                <select className="pr-select" value={reasonId} onChange={(e) => setReasonId(e.target.value)} disabled={reasonsLoading || submitting}>
                  <option value="" disabled>
                    {reasonsLoading ? "Loading…" : "Select one…"}
                  </option>
                  {reasons.map((r) => (
                    <option key={r.PatientRequestReasonID} value={String(r.PatientRequestReasonID)}>
                      {r.Name}
                    </option>
                  ))}
                </select>
                {reasonsError ? <div className="pr-helper pr-help--error">Could not load reasons: {reasonsError}</div> : null}
              </div>

              <div className="pr-field pr-span-2">
                <div className="pr-info">
                  <b>How your information will be used</b>
                  <div className="pr-helper">
                    The information you provide will be used to process and verify your family doctor attachment request, send you secure status updates, allow the selected clinic to contact you about your request and next steps, and support non-identifying aggregate program reporting.
                  </div>
                </div>
              </div>

              <div className="pr-field pr-span-2">
                <label className="pr-label">Consent *</label>
                <label className="pr-consent">
                  <input type="checkbox" checked={consent} disabled={submitting} onChange={(e) => setConsent(e.target.checked)} />
                  <span className="pr-consent-text">
                    I confirm that I am submitting this request voluntarily and that the information provided is accurate. If I am submitting this request for someone else, I confirm that I am authorized to do so. By submitting this request, I consent to AcceptingMD and the selected clinic collecting, using, and disclosing the information I provide to process and verify this family doctor attachment request, send secure status updates, and allow the clinic to contact me about the request and next steps. I understand that submitting a request does not guarantee attachment to the practice and that I should not include detailed medical information in this form. I agree to the{" "}
                    <Link to="/public/privacy" className="pr-link">
                      Privacy Policy
                    </Link>{" "}
                    and{" "}
                    <Link to="/public/terms" className="pr-link">
                      Terms of Use
                    </Link>
                    .
                  </span>
                </label>
              </div>
            </div>

            <div className="pr-actions pr-span-2">
              <button type="submit" className="pr-btn pr-btn-primary pr-btn-full" disabled={!canSubmit}>
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>

            <div className="pr-sticky-submit">
              <button type="submit" className="pr-btn pr-btn-primary" disabled={!canSubmit}>
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}